import { Icon, IconLoader } from "@platform/ui/components";
import { focusRingStyles } from "@platform/ui/styles";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { type DialogProps } from "@radix-ui/react-dialog";
import * as CommandPrimitive from "cmdk";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from "~/shared/components/ds/Dialog";

export const Command = CommandPrimitive.Command;

interface CommandDialogProps extends DialogProps {}
export const CommandDialog = ({ children, ...rest }: CommandDialogProps) => {
  return (
    <Dialog {...rest}>
      <DialogPortal>
        <DialogOverlay className="inset-0 block" />
        <DialogContent
          className="data-[state=closed]:slide-out-to-top-[13%] data-[state=open]:slide-in-from-top-[13%] fixed top-[15%] left-1/2 -translate-x-1/2 overflow-clip p-0"
          {...rest}
        >
          {children}
          <DialogPrimitive.Close
            className={focusRingStyles({
              className:
                "ring-offset-ds-surface-foundation absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none",
            })}
          >
            <Icon className="size-4" aria-hidden symbol="close-line" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

interface CommandInputProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandInput> {
  loading?: boolean;
}
export const CommandInput = React.forwardRef<React.ElementRef<typeof CommandPrimitive.CommandInput>, CommandInputProps>(
  ({ className, loading = false, ...rest }, ref) => (
    <div className="border-ds-stroke-secondary flex items-center border-b px-3" cmdk-input-wrapper="">
      <div className="mr-2 h-4 w-4 shrink-0 opacity-50">
        {loading ? <IconLoader animate className="size-4" /> : <Icon className="size-4" symbol="search-line" />}
      </div>
      <CommandPrimitive.CommandInput
        ref={ref}
        className={twMerge(
          "placeholder:text-ds-text-tertiary flex h-12 w-full rounded-md bg-transparent text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...rest}
      />
    </div>
  ),
);
CommandInput.displayName = CommandPrimitive.CommandInput.displayName;

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandList>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandList>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandList
    ref={ref}
    className={twMerge("max-h-[300px] overflow-x-hidden overflow-y-auto", className)}
    {...rest}
  />
));
CommandList.displayName = CommandPrimitive.CommandList.displayName;

export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandEmpty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandEmpty>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandEmpty
    ref={ref}
    className={twMerge("text-ds-text-primary py-6 text-center text-sm", className)}
    {...rest}
  />
));
CommandEmpty.displayName = CommandPrimitive.CommandEmpty.displayName;

export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandGroup>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandGroup>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandGroup
    ref={ref}
    className={twMerge("text-ds-text-primary overflow-hidden px-2 py-1", className)}
    {...rest}
  />
));
CommandGroup.displayName = CommandPrimitive.CommandGroup.displayName;

export const CommandGroupHeading = React.forwardRef<React.ElementRef<"span">, React.ComponentPropsWithoutRef<"span">>(
  ({ className, ...rest }, ref) => (
    <span
      ref={ref}
      className={twMerge("text-ds-text-tertiary block px-2 py-1.5 text-xs font-medium", className)}
      {...rest}
    />
  ),
);
CommandGroupHeading.displayName = "CommandGroupHeading";

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandSeparator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandSeparator>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandSeparator
    ref={ref}
    className={twMerge("bg-ds-stroke-primary -mx-1 h-px", className)}
    {...rest}
  />
));
CommandSeparator.displayName = CommandPrimitive.CommandSeparator.displayName;

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandItem>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandItem>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandItem
    ref={ref}
    className={twMerge(
      "aria-selected:bg-ds-surface-2 relative flex cursor-default items-center rounded-md px-2 py-3 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...rest}
  />
));
CommandItem.displayName = CommandPrimitive.CommandItem.displayName;
